import { useEffect, useRef, useState } from 'react';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';

interface PayPalNamespace {
  FUNDING: { PAYPAL: string; CARD: string };
  Buttons(opts: {
    fundingSource?: string;
    style?: Record<string, string>;
    createOrder: () => Promise<string>;
    onApprove: (data: { orderID: string }) => Promise<void>;
    onError: (err: unknown) => void;
  }): { render(el: HTMLElement): Promise<void>; close?: () => void };
}

let sdk: Promise<PayPalNamespace> | null = null;
function cargarSdk(clientId: string): Promise<PayPalNamespace> {
  sdk ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=MXN&intent=capture&locale=es_MX&components=buttons&enable-funding=card`;
    s.onload = () => resolve((window as unknown as { paypal: PayPalNamespace }).paypal);
    s.onerror = () => reject(new Error('No se pudo cargar PayPal'));
    document.head.appendChild(s);
  });
  return sdk;
}

/**
 * Botón de PayPal: el navegador solo aprueba; la API crea y captura la orden (plan §10.3).
 * `fuente="paypal"` = PayPal Standard (cuenta PayPal); `fuente="card"` = PayPal Plus (tarjeta
 * sin cuenta). La API etiqueta el pago según lo que confirma PayPal, no según este botón.
 */
export function PayPalBotones({
  clientId,
  fuente,
  body,
  onPagado,
  onError,
}: {
  clientId: string;
  fuente: 'paypal' | 'card';
  body: { membershipId: number; couponCode?: string; scholarshipId?: number };
  onPagado: (folio: string) => void;
  onError: (mensaje: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [cargando, setCargando] = useState(true);
  const datos = JSON.stringify(body);

  useEffect(() => {
    let vivo = true;
    let botones: { close?: () => void } | undefined;
    cargarSdk(clientId)
      .then((paypal) => {
        if (!vivo || !ref.current) return;
        ref.current.innerHTML = '';
        const b = paypal.Buttons({
          fundingSource: fuente === 'card' ? paypal.FUNDING.CARD : paypal.FUNDING.PAYPAL,
          style: { layout: 'vertical', shape: 'pill', label: 'pay' },
          createOrder: async () =>
            (
              await unwrap<{ orderId: string }>(
                api.POST('/api/v1/payments/paypal/orders', {
                  body: JSON.parse(datos) as typeof body,
                }),
              )
            ).orderId,
          onApprove: async ({ orderID }) => {
            try {
              const r = await unwrap<{ folio: string }>(
                api.POST('/api/v1/payments/paypal/orders/{id}/capture', {
                  params: { path: { id: orderID } },
                }),
              );
              onPagado(r.folio);
            } catch (err) {
              onError(mensajeDe(err));
            }
          },
          onError: () =>
            onError('PayPal no pudo procesar el pago. Intenta de nuevo o usa otro método.'),
        });
        botones = b;
        return b.render(ref.current);
      })
      .catch(() => onError('No pudimos cargar PayPal. Revisa tu conexión.'))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
      botones?.close?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, datos, fuente]);

  return (
    <div>
      {cargando && <p className="text-sm text-texto-2">Cargando PayPal…</p>}
      <div ref={ref} className="mx-auto max-w-sm rounded-xl bg-white p-3" />
    </div>
  );
}
